import {
  Box,
  Flex,
  Stack,
  Text,
  PrimaryButton,
  SystemProps,
  Tag,
} from "flicket-ui";
import { Icon } from "~components";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import PriceWithFee from "~components/reservation/common/common.PriceWithFee";
import { DetailsModal } from "../DetailsModal/DetailsModal";
import { forwardRef, MouseEvent, useState } from "react";
import { pick } from "@styled-system/props";
import { TicketTypeOnSaleStatus } from "~graphql/sdk";
import { ImageSlider } from "~features/reservation/non-seated/ImageSlider";
import { TextPreviewRender } from "../common/TextRenders/TextRenders";
import { useIntersectZone } from "~features/reservation/non-seated/useIntersection.hook";
import { hasTextSelection } from "../../../../lib/helpers/textSelection";
import { updateUnsyncedQueryParams } from "~lib/state-stores/appStore/action.updateUnsyncedQueryParams";
import { removeUnsyncedQueryParams } from "~lib/state-stores/appStore/action.removeUnsyncedQueryParams";
import { useTheme } from "styled-components";
import { Info } from "@phosphor-icons/react";
import { useOrganization } from "~hooks";
import { TicketSelectionControl } from "../TicketSelectionControl";
import { TicketAvailabilityStatus } from "~graphql/typed-document-nodes";

interface INonSeatedListItem {
  zoneId: string;
  ticket: IReleaseTicket;
  onChange: (value: number, ticketId: string, zoneId: string) => void;
  quantity: number;
  showHiddenFees: boolean;
  showHighDemandBadge: boolean;
  onClick?: () => void;
  isFirstItem: boolean;
  isLastItem: boolean;
  isPOSRelease: boolean;
}

const Card = forwardRef(
  (
    props: {
      id: string;
      onClick: () => void;
      children: React.ReactNode;
      hasGalleryItem: boolean;
      isLastItem?: boolean;
    },
    ref
  ) => {
    const { id, onClick, children, hasGalleryItem, isLastItem = false } = props;

    return (
      <Box
        ref={ref}
        id={id}
        borderTop="1px solid"
        borderTopColor={{
          _: hasGalleryItem ? "transparent" : "N200",
          xs: "N200",
        }}
        borderBottom="1px solid"
        borderBottomColor={isLastItem ? "N200" : "transparent"}
        onClick={onClick}
        cursor={onClick ? "pointer" : "auto"}
        css={`
          transition: box-shadow 0.15s;
          -webkit-tap-highlight-color: transparent;
          &:hover {
            .slider-navigation {
              opacity: 0.8;
            }
          }
        `}
      >
        <Flex alignItems="center" py={2} flex={1}>
          {children}
        </Flex>
      </Box>
    );
  }
);

Card.displayName = "Card";

export const ExperienceSalesTicketTypeItem = ({
  ticket,
  zoneId,
  onChange,
  quantity,
  showHiddenFees,
  showHighDemandBadge,
  isLastItem,
  isPOSRelease,
}: INonSeatedListItem) => {
  const theme = useTheme();

  const { ref: intersectionRef } = useIntersectZone(
    {
      onIntersectTop: () => {
        updateUnsyncedQueryParams({ zoneId });
      },
    },
    {
      // target bottom only for these elements
      threshold: 0,
    }
  );

  function handleItemClick() {
    if (hasTextSelection()) return;
    updateUnsyncedQueryParams({ modalId: ticket.id });
  }

  const hasGalleryItem = ticket.imageGallery?.length > 0;

  const [canClickItem, setCanClickItem] = useState(hasGalleryItem);

  function handleTextPreviewRender(hasContentOverflow: boolean) {
    setCanClickItem(canClickItem || hasContentOverflow);
  }

  return (
    <>
      <Card
        ref={isLastItem ? intersectionRef : undefined}
        id={ticket.id}
        onClick={canClickItem ? handleItemClick : undefined}
        hasGalleryItem={isPOSRelease ? false : hasGalleryItem}
        isLastItem={isLastItem}
        css={`
          &:hover {
            .slider-navigation {
              opacity: 0.8;
            }
          }
        `}
      >
        <Stack
          gap={2}
          direction={["vertical", "vertical", "horizontal"]}
          justifyContent="space-between"
          flex={1}
        >
          {ticket.imageGallery?.length > 0 && !isPOSRelease && (
            <Box>
              <ImageSlider
                imageGallery={ticket.imageGallery}
                aspectRatio="3 / 2"
                navigationSize="sm"
                borderRadius="sm"
                width={{ _: "100%", xs: 196 }}
                imageProps={{
                  objectFit: "cover",
                  sizes: `
                    (max-width: ${theme.breakpoints.xs640}) 100vw, 
                    (max-width: ${theme.breakpoints.sm768}) 50vw, 
                    20vw
                  `,
                }}
              />
            </Box>
          )}
          <Stack direction="vertical" gap="1/2" flex={1}>
            <Stack
              gap={3}
              direction="horizontal"
              justifyContent="space-between"
            >
              <Stack
                flex={1}
                direction={
                  isPOSRelease
                    ? { _: "vertical", sm: "horizontal" }
                    : "vertical"
                }
                gap="1/2"
                justifyContent="space-between"
                alignContent="center"
              >
                <Text
                  variant={isPOSRelease ? "regular" : "header.XS"}
                  display="flex"
                  alignItems="center"
                >
                  {ticket.name}
                  {isPOSRelease && canClickItem && (
                    <Icon
                      icon={<Info size={16} weight="light" />}
                      ml={1}
                      cursor="pointer"
                      onClick={handleItemClick}
                    />
                  )}
                </Text>
                <Stack
                  gap={["1/2", "1/2", "1/2", 1]}
                  alignItems={{ _: "flex-start", sm: "center" }}
                  direction={{ _: "vertical", sm: "horizontal" }}
                >
                  <TicketPriceAndFee
                    ticket={ticket}
                    showFees={showHiddenFees}
                  />

                  {showHighDemandBadge && !isPOSRelease && (
                    <Tag
                      mb={{ _: "1/2", sm: 0 }}
                      backgroundColor={ticket.customBadgeBackgroundColor as any}
                      css={{ color: ticket.customBadgeTextColor as any }}
                      size="sm"
                    >
                      {ticket.customBadge}
                    </Tag>
                  )}
                </Stack>

                {ticket.availabilityStatus ===
                  TicketAvailabilityStatus.AllocationExhausted && (
                  <Text variant="small" color="error">
                    All tickets are currently in carts. Please try again later.
                  </Text>
                )}
              </Stack>

              <Box
                onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              >
                <TicketSelectionControl
                  ticket={ticket}
                  quantity={quantity}
                  onChange={(value: number) =>
                    onChange(value, ticket.id, ticket.zoneId)
                  }
                  variant="inline"
                />
              </Box>
            </Stack>
            {!isPOSRelease && (
              <TextPreviewRender
                value={ticket.description}
                lines={2}
                onRender={handleTextPreviewRender}
                pr={{ _: 0, sm: "140px" } as any}
                variant="small"
              />
            )}
          </Stack>
        </Stack>
      </Card>

      <Modal
        ticketType={ticket}
        quantity={quantity}
        showHiddenFees={showHiddenFees}
        onChange={onChange}
      />
    </>
  );
};

function Modal({
  ticketType,
  showHiddenFees,
  quantity,
  onChange,
}: {
  ticketType: IReleaseTicket;
  showHiddenFees: boolean;
  quantity: number;
  onChange: INonSeatedListItem["onChange"];
}) {
  const [tempQuantity, setTempQuantity] = useState(quantity ?? 1);

  function onSubmit() {
    onChange(tempQuantity, ticketType.id, ticketType.zoneId);
    removeUnsyncedQueryParams(["modalId"]);
  }

  return (
    <DetailsModal
      id={ticketType.id}
      title={ticketType.name}
      description={ticketType.description}
      imageGallery={ticketType.imageGallery}
      aspectRatio={"3 / 2"}
      stickySection={({ isSticky }) => (
        <Stack
          px={isSticky ? { _: 2, sm: 4 } : 0}
          gap={3}
          direction="horizontal"
          justifyContent="space-between"
          flex={1}
          alignItems="center"
        >
          <Box>
            <Text as="h1" variant="header.M">
              {ticketType.name}
            </Text>
            <Text variant="regular">
              <TicketPriceAndFee
                ticket={ticketType}
                showFees={showHiddenFees}
              />
            </Text>
          </Box>
        </Stack>
      )}
      footer={
        <TicketTypeQunatitySelect
          ticketType={ticketType}
          quantity={quantity}
          tempQuantity={tempQuantity}
          onChange={setTempQuantity}
          onSubmit={onSubmit}
        />
      }
    />
  );
}

function TicketTypeQunatitySelect({
  ticketType,
  quantity,
  tempQuantity,
  onChange,
  onSubmit,
}: {
  ticketType: IReleaseTicket;
  quantity: number;
  tempQuantity: number;
  onChange: (q: number) => void;
  onSubmit: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonLabel] = useState(
    quantity === undefined ? "Add to order" : "Update order"
  );

  const disabled =
    ticketType.onSaleStatus !== TicketTypeOnSaleStatus.OnSale ||
    (ticketType.availabilityStatus &&
      ticketType.availabilityStatus !== TicketAvailabilityStatus.Available);

  return (
    <>
      <Box>
        <Stack gap={2} alignItems="center">
          <TicketSelectionControl
            ticket={ticketType}
            quantity={tempQuantity}
            onChange={onChange}
            variant="modal"
            allowZero={
              !ticketType.availabilityStatus ||
              ticketType.availabilityStatus ===
                TicketAvailabilityStatus.Available
            }
            disableDecrement={tempQuantity === undefined}
          />
          {!disabled && (
            <PrimaryButton
              disabled={isSubmitting}
              w="150px"
              onClick={() => {
                setIsSubmitting(true);
                onSubmit();
              }}
            >
              {buttonLabel}
            </PrimaryButton>
          )}
        </Stack>
      </Box>
    </>
  );
}

function TicketPriceAndFee({
  ticket,
  showFees,
  ...props
}: {
  showFees: boolean;
  ticket: IReleaseTicket;
} & SystemProps) {
  const { organization } = useOrganization();
  if (ticket.isSoldOut) {
    return null;
  }

  // Hack: Urban polo want all $0 tickets to show "POA" (Price on Application)
  if (
    [
      "aac14032-ac60-4970-b282-59d9f828d8a3",
      "9e163fd6-be92-4297-9345-b17469c770ca",
      "71508fa5-ed17-4d3d-933c-02afc931163c",
      "9e9345e0-8428-4c8d-8775-f6685888be84",
      "43e80c9a-3d39-485a-9a90-da17f15d3136",
      "9e775d37-5168-4896-8ff4-3c67e0e3b0b8",
    ].includes(organization.id) &&
    !ticket.price
  ) {
    return (
      <Box {...pick(props)}>
        <Text as="span" variant="regular">
          POA
        </Text>
      </Box>
    );
  }

  return (
    <Box {...pick(props)}>
      <Text as="span" variant="regular">
        <PriceWithFee
          price={ticket.price}
          showFees={showFees}
          isSoldOut={ticket.isSoldOut}
          fee={ticket.fee}
          currency={organization.currency}
        />
      </Text>
    </Box>
  );
}
